import React, { useState } from 'react';
import { BuildingIcon, HandshakeIcon, SearchIcon, PlusIcon, CheckIcon, XIcon, UserIcon, PhoneIcon, MailIcon, LinkIcon, UnlinkIcon, ClockIcon, AlertCircleIcon, InfoIcon, LockIcon, CheckCircleIcon, Trash2Icon } from 'lucide-react';
interface BusinessPartner {
  id: string;
  name: string;
  company: string;
  role: 'broker' | 'contractor';
  email: string;
  phone: string;
  status: 'active' | 'pending' | 'inactive';
  connectedSince: string;
}
interface TruckingCompanyProfileProps {
  businessPartners: BusinessPartner[];
  onAddPartner: (partner: Omit<BusinessPartner, 'id' | 'connectedSince' | 'status'>) => void;
  onUpdatePartnerStatus: (partnerId: string, status: 'active' | 'pending' | 'inactive') => void;
  onRemovePartner: (partnerId: string) => void;
}
export const TruckingCompanyProfile = ({
  businessPartners,
  onAddPartner,
  onUpdatePartnerStatus,
  onRemovePartner
}: TruckingCompanyProfileProps) => {
  const [showAddPartnerForm, setShowAddPartnerForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'company' | 'password' | 'connections'>('connections');
  const [searchQuery, setSearchQuery] = useState('');
  // Company information state
  const [companyInfo, setCompanyInfo] = useState({
    name: 'Mountain View Trucking',
    address: '123 Trucking Lane, Salt Lake City, UT 84101',
    contactName: 'Tom Trucking',
    phone: '555-123-4567',
    email: 'info@mountainviewtrucking.com',
    website: 'www.mountainviewtrucking.com',
    taxId: '12-3456789',
    mcNumber: 'MC-123456',
    dotNumber: 'DOT-7891234'
  });
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  // Filter partners based on search query
  const filteredPartners = searchQuery ? businessPartners.filter(partner => partner.name.toLowerCase().includes(searchQuery.toLowerCase()) || partner.company.toLowerCase().includes(searchQuery.toLowerCase())) : businessPartners;
  // Handle company info form change
  const handleCompanyInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setCompanyInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle password form change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle company info update
  const handleUpdateCompanyInfo = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would send the updated info to the server
    alert('Company information updated successfully!');
  };
  // Handle password update
  const handleUpdatePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      alert('Password must be at least 8 characters long!');
      return;
    }
    // In a real app, this would send the password update to the server
    alert('Password updated successfully!');
    // Reset form
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };
  return <div className="bg-white rounded-lg shadow" data-id="element-4223">
      {/* Profile Tabs */}
      <div className="border-b" data-id="element-4224">
        <div className="flex" data-id="element-4225">
          <button className={`px-4 py-3 font-medium text-sm flex items-center gap-2 ${activeTab === 'company' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-800'}`} onClick={() => setActiveTab('company')} data-id="element-4226">
            <BuildingIcon size={16} data-id="element-4227" />
            Company Information
          </button>
          <button className={`px-4 py-3 font-medium text-sm flex items-center gap-2 ${activeTab === 'password' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-800'}`} onClick={() => setActiveTab('password')} data-id="element-4228">
            <LockIcon size={16} data-id="element-4229" />
            Change Password
          </button>
          <button className={`px-4 py-3 font-medium text-sm flex items-center gap-2 ${activeTab === 'connections' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-800'}`} onClick={() => setActiveTab('connections')} data-id="element-4230">
            <HandshakeIcon size={16} data-id="element-4231" />
            Business Connections
          </button>
        </div>
      </div>

      {/* Company Information Tab */}
      {activeTab === 'company' && <div className="p-6" data-id="element-4232">
          <h2 className="text-lg font-bold mb-4" data-id="element-4233">Company Information</h2>
          <form onSubmit={handleUpdateCompanyInfo} data-id="element-4234">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-4235">
              <div data-id="element-4236">
                <label className="block text-sm font-medium mb-1" data-id="element-4237">
                  Company Name
                </label>
                <input type="text" name="name" value={companyInfo.name} onChange={handleCompanyInfoChange} className="w-full p-2 border border-gray-300 rounded-md" required data-id="element-4238" />
              </div>
              <div data-id="element-4239">
                <label className="block text-sm font-medium mb-1" data-id="element-4240">
                  Address
                </label>
                <input type="text" name="address" value={companyInfo.address} onChange={handleCompanyInfoChange} className="w-full p-2 border border-gray-300 rounded-md" required data-id="element-4241" />
              </div>
              <div data-id="element-4242">
                <label className="block text-sm font-medium mb-1" data-id="element-4243">
                  Contact Name
                </label>
                <input type="text" name="contactName" value={companyInfo.contactName} onChange={handleCompanyInfoChange} className="w-full p-2 border border-gray-300 rounded-md" required data-id="element-4244" />
              </div>
              <div data-id="element-4245">
                <label className="block text-sm font-medium mb-1" data-id="element-4246">Phone</label>
                <input type="tel" name="phone" value={companyInfo.phone} onChange={handleCompanyInfoChange} className="w-full p-2 border border-gray-300 rounded-md" required data-id="element-4247" />
              </div>
              <div data-id="element-4248">
                <label className="block text-sm font-medium mb-1" data-id="element-4249">Email</label>
                <input type="email" name="email" value={companyInfo.email} onChange={handleCompanyInfoChange} className="w-full p-2 border border-gray-300 rounded-md" required data-id="element-4250" />
              </div>
              <div data-id="element-4251">
                <label className="block text-sm font-medium mb-1" data-id="element-4252">
                  Website
                </label>
                <input type="text" name="website" value={companyInfo.website} onChange={handleCompanyInfoChange} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-4253" />
              </div>
              <div data-id="element-4254">
                <label className="block text-sm font-medium mb-1" data-id="element-4255">
                  Tax ID / EIN
                </label>
                <input type="text" name="taxId" value={companyInfo.taxId} onChange={handleCompanyInfoChange} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-4256" />
              </div>
              <div data-id="element-4257">
                <label className="block text-sm font-medium mb-1" data-id="element-4258">
                  MC Number
                </label>
                <input type="text" name="mcNumber" value={companyInfo.mcNumber} onChange={handleCompanyInfoChange} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-4259" />
              </div>
              <div data-id="element-4260">
                <label className="block text-sm font-medium mb-1" data-id="element-4261">
                  DOT Number
                </label>
                <input type="text" name="dotNumber" value={companyInfo.dotNumber} onChange={handleCompanyInfoChange} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-4262" />
              </div>
            </div>
            <div className="flex justify-end" data-id="element-4263">
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-4264">
                Save Changes
              </button>
            </div>
          </form>
        </div>}

      {/* Change Password Tab */}
      {activeTab === 'password' && <div className="p-6" data-id="element-4265">
          <h2 className="text-lg font-bold mb-4" data-id="element-4266">Change Password</h2>
          <form onSubmit={handleUpdatePassword} className="max-w-md" data-id="element-4267">
            <div className="space-y-4 mb-6" data-id="element-4268">
              <div data-id="element-4269">
                <label className="block text-sm font-medium mb-1" data-id="element-4270">
                  Current Password
                </label>
                <input type="password" name="currentPassword" value={passwordForm.currentPassword} onChange={handlePasswordChange} className="w-full p-2 border border-gray-300 rounded-md" required data-id="element-4271" />
              </div>
              <div data-id="element-4272">
                <label className="block text-sm font-medium mb-1" data-id="element-4273">
                  New Password
                </label>
                <input type="password" name="newPassword" value={passwordForm.newPassword} onChange={handlePasswordChange} className="w-full p-2 border border-gray-300 rounded-md" required minLength={8} data-id="element-4274" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-4275">
                  Password must be at least 8 characters long
                </p>
              </div>
              <div data-id="element-4276">
                <label className="block text-sm font-medium mb-1" data-id="element-4277">
                  Confirm New Password
                </label>
                <input type="password" name="confirmPassword" value={passwordForm.confirmPassword} onChange={handlePasswordChange} className="w-full p-2 border border-gray-300 rounded-md" required data-id="element-4278" />
              </div>
            </div>
            <div className="flex justify-end" data-id="element-4279">
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-4280">
                Update Password
              </button>
            </div>
          </form>
        </div>}

      {/* Business Connections Tab */}
      {activeTab === 'connections' && <div className="p-6" data-id="element-4281">
          <div className="flex justify-between items-center mb-6" data-id="element-4282">
            <h2 className="text-lg font-bold" data-id="element-4283">Business Connections</h2>
            <button onClick={() => setShowAddPartnerForm(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2" data-id="element-4284">
              <PlusIcon size={16} data-id="element-4285" />
              Add Connection
            </button>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6" data-id="element-4286">
            <div className="flex items-start gap-3" data-id="element-4287">
              <InfoIcon size={20} className="text-blue-500 mt-1 flex-shrink-0" data-id="element-4288" />
              <div data-id="element-4289">
                <p className="text-blue-800 font-medium" data-id="element-4290">
                  Managing Your Business Connections
                </p>
                <p className="text-blue-700 mt-1 text-sm" data-id="element-4291">
                  Connect with brokers and contractors to receive job
                  assignments and expand your business network. You can work
                  with multiple partners simultaneously.
                </p>
              </div>
            </div>
          </div>

          {/* Search and filter */}
          <div className="mb-6" data-id="element-4292">
            <div className="relative" data-id="element-4293">
              <input type="text" placeholder="Search connections..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full p-2 pl-10 border border-gray-300 rounded-md" data-id="element-4294" />
              <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-4295" />
            </div>
          </div>

          {/* Connections list */}
          <div className="border rounded-md overflow-hidden" data-id="element-4296">
            <div className="bg-gray-50 px-4 py-3 border-b" data-id="element-4297">
              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-600" data-id="element-4298">
                <div className="col-span-3" data-id="element-4299">COMPANY</div>
                <div className="col-span-2" data-id="element-4300">ROLE</div>
                <div className="col-span-3" data-id="element-4301">CONTACT</div>
                <div className="col-span-2" data-id="element-4302">STATUS</div>
                <div className="col-span-2" data-id="element-4303">ACTIONS</div>
              </div>
            </div>
            {filteredPartners.length === 0 ? <div className="p-6 text-center" data-id="element-4304">
                <HandshakeIcon size={40} className="mx-auto text-gray-400 mb-2" data-id="element-4305" />
                <p className="text-gray-600 mb-1" data-id="element-4306">No connections found</p>
                <p className="text-sm text-gray-500" data-id="element-4307">
                  Add business connections to receive job assignments
                </p>
              </div> : <div className="divide-y" data-id="element-4308">
                {filteredPartners.map(partner => <div key={partner.id} className="px-4 py-3 hover:bg-gray-50" data-id="element-4309">
                    <div className="grid grid-cols-12 gap-4 items-center" data-id="element-4310">
                      <div className="col-span-3" data-id="element-4311">
                        <div className="font-medium" data-id="element-4312">{partner.company}</div>
                        <div className="text-xs text-gray-500" data-id="element-4313">
                          Connected since{' '}
                          {new Date(partner.connectedSince).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="col-span-2" data-id="element-4314">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${partner.role === 'broker' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-4315">
                          {partner.role === 'broker' ? 'Broker' : 'Contractor'}
                        </span>
                      </div>
                      <div className="col-span-3" data-id="element-4316">
                        <div className="text-sm" data-id="element-4317">{partner.name}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1" data-id="element-4318">
                          <MailIcon size={12} data-id="element-4319" /> {partner.email}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1" data-id="element-4320">
                          <PhoneIcon size={12} data-id="element-4321" /> {partner.phone}
                        </div>
                      </div>
                      <div className="col-span-2" data-id="element-4322">
                        {partner.status === 'active' && <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center gap-1 w-fit" data-id="element-4323">
                            <CheckCircleIcon size={12} data-id="element-4324" />
                            Active
                          </span>}
                        {partner.status === 'pending' && <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium flex items-center gap-1 w-fit" data-id="element-4325">
                            <ClockIcon size={12} data-id="element-4326" />
                            Pending
                          </span>}
                        {partner.status === 'inactive' && <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium flex items-center gap-1 w-fit" data-id="element-4327">
                            <XIcon size={12} data-id="element-4328" />
                            Inactive
                          </span>}
                      </div>
                      <div className="col-span-2" data-id="element-4329">
                        <div className="flex gap-2" data-id="element-4330">
                          {partner.status === 'pending' && <button onClick={() => onUpdatePartnerStatus(partner.id, 'active')} className="text-green-600 hover:text-green-800" title="Approve connection" data-id="element-4331">
                              <CheckIcon size={18} data-id="element-4332" />
                            </button>}
                          {partner.status === 'active' && <button onClick={() => onUpdatePartnerStatus(partner.id, 'inactive')} className="text-orange-600 hover:text-orange-800" title="Deactivate connection" data-id="element-4333">
                              <UnlinkIcon size={18} data-id="element-4334" />
                            </button>}
                          {partner.status === 'inactive' && <button onClick={() => onUpdatePartnerStatus(partner.id, 'active')} className="text-blue-600 hover:text-blue-800" title="Reactivate connection" data-id="element-4335">
                              <LinkIcon size={18} data-id="element-4336" />
                            </button>}
                          <button onClick={() => onRemovePartner(partner.id)} className="text-red-600 hover:text-red-800" title="Remove connection" data-id="element-4337">
                            <Trash2Icon size={18} data-id="element-4338" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>}
          </div>
        </div>}

      {/* Add Partner Modal */}
      {showAddPartnerForm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-4339">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md" data-id="element-4340">
            <div className="flex items-center justify-between p-4 border-b" data-id="element-4341">
              <h3 className="text-lg font-medium" data-id="element-4342">Add Business Connection</h3>
              <button onClick={() => setShowAddPartnerForm(false)} className="text-gray-500 hover:text-gray-700" data-id="element-4343">
                <XIcon size={20} data-id="element-4344" />
              </button>
            </div>
            <AddPartnerForm onAddPartner={partner => {
          onAddPartner(partner);
          setShowAddPartnerForm(false);
        }} onCancel={() => setShowAddPartnerForm(false)} data-id="element-4345" />
          </div>
        </div>}
    </div>;
};
interface AddPartnerFormProps {
  onAddPartner: (partner: Omit<BusinessPartner, 'id' | 'connectedSince' | 'status'>) => void;
  onCancel: () => void;
}
const AddPartnerForm = ({
  onAddPartner,
  onCancel
}: AddPartnerFormProps) => {
  const [formData, setFormData] = useState<Omit<BusinessPartner, 'id' | 'connectedSince' | 'status'>>({
    name: '',
    company: '',
    role: 'broker',
    email: '',
    phone: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAddPartner(formData);
  };
  return <form onSubmit={handleSubmit} data-id="element-4346">
      <div className="p-4 space-y-4" data-id="element-4347">
        <div data-id="element-4348">
          <label className="block text-sm font-medium mb-1" data-id="element-4349">
            Business Type
          </label>
          <select name="role" value={formData.role} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" required data-id="element-4350">
            <option value="broker" data-id="element-4351">Broker</option>
            <option value="contractor" data-id="element-4352">Contractor</option>
          </select>
        </div>
        <div data-id="element-4353">
          <label className="block text-sm font-medium mb-1" data-id="element-4354">Company Name</label>
          <input type="text" name="company" value={formData.company} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" placeholder="Enter company name" required data-id="element-4355" />
        </div>
        <div data-id="element-4356">
          <label className="block text-sm font-medium mb-1" data-id="element-4357">
            Contact Person
          </label>
          <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" placeholder="Enter contact name" required data-id="element-4358" />
        </div>
        <div data-id="element-4359">
          <label className="block text-sm font-medium mb-1" data-id="element-4360">Email</label>
          <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" placeholder="Enter email address" required data-id="element-4361" />
        </div>
        <div data-id="element-4362">
          <label className="block text-sm font-medium mb-1" data-id="element-4363">Phone</label>
          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" placeholder="Enter phone number" required data-id="element-4364" />
        </div>
      </div>
      <div className="bg-gray-50 px-4 py-3 flex justify-end gap-3 border-t" data-id="element-4365">
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4366">
          Cancel
        </button>
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-4367">
          Send Connection Request
        </button>
      </div>
    </form>;
};